import {
  REMEDIATION_STEP_LABELS, outcomeCauseLabel,
  type AnomalyBoots, type AnomalyLastAction, type AnomalyLine, type AnomalyReconnects, type AnomalyRow, type AnomalyStartSuccess, type AnomalyType,
  type AnomalyWaitingOn, type PlatformEventView,
} from '@evi/shared';

/**
 * The anomalies page in Admin's words (Warren, 28 Oct 2026, addendum item 3: real reboots and reconnects apart, our
 * own rolls and network events left out, a whole site down as one row, the last automatic action and who we are
 * waiting on; item 2: start success on the page). Pure, so the words are tested without rendering. What a caller may
 * read is the Api's decision (lib/anomalies/project.ts): a field the Api left out is simply not shown.
 */

export const ANOMALY_TYPE_LABELS: Readonly<Record<AnomalyType, string>> = {
  faulted: 'Faulted',
  offline: 'Offline',
  unstable_boot: 'Rebooting',
  error_spike: 'Errors',
  reboot_loop: 'Reboot loop',
  unstable_link: 'Losing connection',
  site_down: 'Site down',
  start_success_low: 'Start success',
  never_connected: 'Never connected',
  site_regression: 'Below its baseline',
  charger_traffic_regression: 'Charger contact below baseline',
};

/** "2d 4h" / "47m": the eye ranks age by it. */
export function formatSince(durationMs: number): string {
  const totalMin = Math.max(0, Math.floor(durationMs / 60_000));
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  if (h < 24) return `${h}h ${totalMin % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/** Down more than a day: the row carries a badge with how long. */
export function longDownBadge(row: Pick<AnomalyRow, 'anomaly_type' | 'duration_ms'>): string | null {
  if (row.anomaly_type !== 'offline' && row.anomaly_type !== 'site_down') return null;
  return row.duration_ms > 24 * 60 * 60_000 ? `${Math.floor(row.duration_ms / (24 * 60 * 60_000))}d` : null;
}

/** 12:13Z on a UTC clock, the way every UTC time on these pages reads. */
export function utcTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? `${d.toISOString().slice(11, 16)}Z` : null;
}

/** "3h ago" from an ISO time. */
export function ago(iso: string | null | undefined, now: Date): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return `${formatSince(now.getTime() - t)} ago`;
}

export interface Cell { text: string; detail: string | null; tone: 'normal' | 'muted' | 'bad' | 'amber' }

const empty: Cell = { text: '-', detail: null, tone: 'muted' };

/** "Reboots 24h": the real reboots; EVI reads the boots behind them. */
export function rebootsCell(b: AnomalyBoots | null | undefined): Cell {
  if (!b) return empty;
  const parts: string[] = [];
  if (b.total !== undefined && b.total > b.reboots) {
    if (b.ours) parts.push(`${b.ours} in our rolls or network events`);
    if (b.triggered) parts.push(`${b.triggered} we asked for`);
    if (b.reset_by_us) parts.push(`${b.reset_by_us} after our reset`);
    if (b.reconnect_boots) parts.push(`${b.reconnect_boots} on a reconnect`);
  }
  const detail = [
    b.on_every_reconnect ? 'This model boots on every reconnect' : null,
    parts.length ? `${b.total} boots: ${parts.join(', ')}, not counted` : null,
  ].filter(Boolean).join('. ') || null;
  return { text: String(b.reboots), detail, tone: b.reboots > 3 ? 'bad' : b.reboots === 0 ? 'muted' : 'normal' };
}

/** "Reconnects 24h": the counted ones; EVI reads the ours and network split. */
export function reconnectsCell(r: AnomalyReconnects | null | undefined): Cell {
  if (!r) return empty;
  const split = r.total !== undefined && r.total > r.counted
    ? `${r.total} in all: ${r.ours ?? 0} ours, ${r.network ?? 0} network, not counted`
    : null;
  return { text: String(r.counted), detail: split, tone: r.counted >= 3 ? 'bad' : r.counted === 0 ? 'muted' : 'normal' };
}

/** 97.9%: floored to a tenth, so a rate below the target never prints as the target. */
export function percent(rate: number): string {
  return `${Math.floor(rate * 1000) / 10}%`;
}

/** "Start success 7d": the rate and the starts behind it, red below the 98% target, the top causes under it. */
export function startSuccessCell(s: AnomalyStartSuccess | null | undefined): Cell {
  if (!s) return empty;
  if (s.rate === null) return { text: 'No starts', detail: null, tone: 'muted' };
  const causes = s.top_causes.length ? s.top_causes.map((c) => `${outcomeCauseLabel(c.cause)} (${c.count})`).join(', ') : null;
  return { text: `${percent(s.rate)} (${s.succeeded} of ${s.counted})`, detail: causes, tone: s.below_target ? 'bad' : 'normal' };
}

const OUTCOME_WORDS: Readonly<Record<string, string>> = {
  would_have_sent: 'would have sent',
  not_connected: 'not connected',
  // Step d is an EVI task (Warren, 4 Nov 2026): "Building" shows on the waiting cell only once EVI sent the draft.
  asked: 'a draft for EVI to send',
  opened: 'service call opened',
};

/** "Soft reset (shadow): would have sent, 2h ago on AL0012...": the last automatic action and its result. */
export function lastActionCell(a: AnomalyLastAction | null | undefined, now: Date): Cell {
  if (!a) return { text: 'None yet', detail: null, tone: 'muted' };
  const what = `${REMEDIATION_STEP_LABELS[a.step] ?? a.step}${a.mode === 'shadow' ? ' (shadow)' : ''}`;
  const outcome = OUTCOME_WORDS[a.outcome] ?? a.outcome.replace(/^skipped:/, 'held back: ').replace(/_/g, ' ');
  const cleared = a.cleared === true ? ', cleared' : a.cleared === false ? ', not cleared' : '';
  const detail = [ago(a.at, now), a.charger_identity ? `on ${a.charger_identity}` : null].filter(Boolean).join(' ');
  return { text: `${what}: ${outcome}${cleared}`, detail: detail || null, tone: a.cleared === false ? 'bad' : 'normal' };
}

const PARTY_WORDS: Readonly<Record<AnomalyWaitingOn['party'], string>> = { building: 'Building', installer: 'Installer', evi: 'EVI' };
const DETAIL_WORDS: Readonly<Record<NonNullable<AnomalyWaitingOn['detail']>, string>> = {
  asked: 'asked to cycle the breaker',
  service_call: 'service call open',
  automatic: 'automatic fixes running',
  our_side: 'our side',
  nobody_yet: 'nobody yet',
};

/** "Building: asked to cycle the breaker (1 of 3)"; EVI ops with nothing open reads amber. */
export function waitingCell(w: AnomalyWaitingOn | null | undefined): Cell {
  if (!w) return empty;
  const party = w.party === 'evi' && (w.detail === 'service_call' || w.detail === 'nobody_yet') ? 'EVI ops' : PARTY_WORDS[w.party];
  const of = w.of && w.of.total > 1 ? ` (${w.of.count} of ${w.of.total})` : '';
  return {
    text: `${party}${of}`,
    detail: w.detail ? DETAIL_WORDS[w.detail] : null,
    tone: w.detail === 'nobody_yet' ? 'amber' : 'normal',
  };
}

const EVENT_WORDS: Readonly<Record<PlatformEventView['kind'], string>> = {
  roll: 'Ocpp roll', crash: 'Ocpp crash', scale_in: 'Ocpp scale-in', unknown: 'Ocpp task change',
  network: 'Network event', wave: 'Many sites at once (our roll or crash, or the network)',
};

/** "Ocpp roll 12:13Z to 12:20Z: 75 chargers at 20 sites. Drops within 10 minutes are not counted." */
export function platformEventLine(e: PlatformEventView): string {
  const from = utcTime(e.started_at);
  const to = utcTime(e.ended_at);
  const span = from && to && from !== to ? `${from} to ${to}` : from ?? '';
  const who = e.chargers > 0 ? `: ${e.chargers} charger${e.chargers === 1 ? '' : 's'}${e.sites > 0 ? ` at ${e.sites} site${e.sites === 1 ? '' : 's'}` : ''}` : '';
  return `${EVENT_WORDS[e.kind]} ${span}${who}`;
}

/**
 * "Our platform failed 2 starts and cut 3 sessions at 4 sites (7 days), owned by EVI, not counted against any site." The
 * starts and the sessions our own roll or crash cut, each named (round 5: the cut sessions read as failed starts).
 */
export function networkFailuresLine(n: { failed: number; sessions_failed?: number; sites: number }): string {
  const plural = (k: number, one: string) => `${k} ${one}${k === 1 ? '' : 's'}`;
  const cut = n.sessions_failed ?? 0;
  const what = [n.failed > 0 ? `failed ${plural(n.failed, 'start')}` : '', cut > 0 ? `cut ${plural(cut, 'session')}` : ''].filter(Boolean).join(' and ') || 'failed 0 starts';
  return `Our platform ${what} at ${plural(n.sites, 'site')} (7 days), owned by EVI, not counted against any site.`;
}

/**
 * The banner's word on the Ocpp task watch: running since its current run began, not running (reconnects read from
 * the 5-minute offline check since its last poll), or never run.
 */
export function watchNote(watchSince: string | null | undefined, watchLastAt: string | null | undefined): string {
  if (watchSince) return `The Ocpp task watch has run since ${new Date(watchSince).toLocaleString()}.`;
  if (watchLastAt) {
    return `The Ocpp task watch is not running: its last poll was ${new Date(watchLastAt).toLocaleString()}, so reconnects since then are read from the 5-minute offline check.`;
  }
  return 'The Ocpp task watch has not run yet.';
}

/** Where a row goes: a charger row to its charger, a site row to its site (a Site persona: My Sites). */
export function rowLink(row: Pick<AnomalyRow, 'scope' | 'charger_id' | 'location_id'>, isSite: boolean, siteDetailPath: (id: string | null) => string): string {
  if (isSite) return siteDetailPath(row.location_id);
  if (row.scope === 'site' && row.location_id) return `/locations/${row.location_id}`;
  return `/chargers/${row.charger_id}`;
}

/** Where a line goes: its charger; a site-wide line (start success at the site) to the site (a Site persona: My Sites). */
export function lineLink(
  line: Pick<AnomalyLine, 'charger_id'>,
  row: Pick<AnomalyRow, 'charger_id' | 'location_id'>,
  isSite: boolean,
  siteDetailPath: (id: string | null) => string,
): string {
  if (isSite) return siteDetailPath(row.location_id);
  if (line.charger_id) return `/chargers/${line.charger_id}`;
  return row.location_id ? `/locations/${row.location_id}` : `/chargers/${row.charger_id}`;
}

/** The row's key: its group, or (a payload from before group rows) the charger and type. */
export function rowKey(row: Pick<AnomalyRow, 'group_key' | 'charger_id' | 'anomaly_type'>): string {
  return row.group_key ?? `${row.charger_id}-${row.anomaly_type}`;
}

/** The Charger or site cell: a site row names the site and how many of its chargers are behind it. */
export function rowTitle(row: Pick<AnomalyRow, 'scope' | 'charger_identity' | 'location_name' | 'charger_count' | 'site_charger_count' | 'lines'>): { title: string; sub: string | null } {
  if (row.scope === 'site') {
    const n = row.charger_count ?? row.lines?.length ?? 0;
    const of = row.site_charger_count ? ` of ${row.site_charger_count}` : '';
    return { title: row.location_name ?? 'Unknown location', sub: n > 0 ? `${n}${of} charger${(row.site_charger_count ?? n) === 1 ? '' : 's'}` : null };
  }
  return { title: row.charger_identity, sub: row.location_name };
}
